import React, { useState } from "react";
import { FileEarmarkExcel } from "react-bootstrap-icons";
import * as XLSX from "xlsx";
import { useLocation } from 'react-router-dom';
import apiService from "../../API/ApiService.js";
import "./Toolbar.css";

// ===================== UTILITY FUNCTIONS =====================

const cleanStepName = (stepName) => {
  if (!stepName) return "";
  return stepName.toLowerCase().trim().replace(/\s+/g, "").replace(/[_-]/g, "");
};

const normalizeFailureName = (failureName) => {
  if (!failureName) return failureName;
  let f = failureName;
  f = f.replace("leftStickX, leftStickY", "leftStick");
  f = f.replace("leftStickY, leftStickX", "leftStick");
  f = f.replace("rightStickX, rightStickY", "rightStick");
  f = f.replace("rightStickY, rightStickX", "rightStick");
  f = f.replace("leftStickX", "leftStick");
  f = f.replace("leftStickY", "leftStick");
  f = f.replace("rightStickX", "rightStick");
  f = f.replace("rightStickY", "rightStick");
  return f;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "";
  try {
    const date = new Date(dateStr.replace("Z", ""));
    return date.toLocaleString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  } catch (error) {
    return dateStr;
  }
};

const parseDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    const [datePart, timePart] = dateStr.split(' ');
    const [day, month, year] = datePart.split('/');
    const [hour, minute] = timePart.split(':');
    return new Date(year, month - 1, day, hour, minute);
  } catch (error) {
    console.error("Date parsing error:", dateStr, error);
    return null;
  }
};

const processStepData = (stepName, stepData) => {
  const stepNameClean = cleanStepName(stepName);
  let failureField = "";
  let status = "PASS";

  if (!stepData || typeof stepData !== 'object') {
    return ["", "FAIL"];
  }

  if (stepNameClean === "stepone") {
    const failKeys = Object.keys(stepData).filter(k => stepData[k] === true);
    if (failKeys.length > 0) {
      failureField = failKeys.join(", ");
      status = "FAIL";
    }
  } else if (stepNameClean === "stepfive") {
    const val = stepData.noSpecialCaseErrorFound;
    if (val !== true) {
      failureField = "noSpecialCaseErrorFound";
      status = "FAIL";
    }
  } else if (stepNameClean === "stepsix") {
    const val = stepData.micAudioCheck;
    if (val !== true) {
      failureField = "micAudioCheck";
      status = "FAIL";
    }
  } else if (stepNameClean === "stepseven") {
    const val = stepData.bluetooth;
    if (val !== true) {
      failureField = "bluetooth";
      status = "FAIL";
    }
  } else {
    const failKeys = Object.keys(stepData).filter(k => 
      stepData[k] === false || stepData[k] === null
    );
    if (failKeys.length > 0) {
      failureField = failKeys.join(", ");
      status = "FAIL";
    }
  }

  return [failureField, status];
};

// ===================== MAIN COMPONENT =====================
const Toolbar = ({ data, visibleColumns, filename, filters }) => {
  const location = useLocation();
  const { username } = location.state || {};
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  const fetchAllData = async () => {
    const trimmedUsername = username?.trim() || "";
    const params = {
      username: trimmedUsername,
      device: filters?.device || "",
      raspberry: filters?.raspberry || "",
      status: filters?.status || "",
      date: filters?.dateRange || [],
      timeStart: filters?.timeRange?.start || "",
      timeEnd: filters?.timeRange?.end || "",
      mode: filters?.mode || "",
    };
    
    const response = await apiService.get("/getDataALL", { params });
    return response.data?.data || [];
  };

  const processSession = (session) => {
    const raspberry = session.raspberry_id || '';
    const serial = session.realSerialNumber || session.deviceSerialNumber || "not found";
    const globalStatus = session.globalStatus || "UNKNOWN";
    const grade = session.grade || "";
    
    if (!serial || serial.toLowerCase() === "not found") return null;
    
    const isQuickMode = raspberry.toLowerCase().includes('quick');
    const modeFilter = filters?.mode || '';
    
    if (modeFilter) {
      const filterLower = modeFilter.toLowerCase();
      if ((filterLower === 'quick' && !isQuickMode) || 
          (filterLower === 'jig' && isQuickMode)) {
        return null;
      }
    }
    
    const startDate = formatDate(session.startDate);
    const endDate = formatDate(session.endDate);
    
    if (isQuickMode) {
      return {
        "DS5_SN": session.realSerialNumber || "",
        "Status": session.globalStatus || "",
        "JOB_ID": session.job_id || "",
        "RECEIPT_DATE": formatDate(session.receipt_date),
        "GRADE": session.grade || "",
        "START_DATE": startDate,
        "TESTER_NAME": raspberry,
        "MAC_ADDRESS": session.deviceSerialNumber || "",
        "TEST_TRACKING": session.testTracking || "",
        "OPERATOR_ID": session.operator_id || "",
        "SYMPTOM": session.symptome_description || "",
        "END_DATE": endDate,
        "OPERATOR_NAME": session.operator_name || "",
        "MODE": "Quick"
      };
    } else {
      let testData;
      try {
        testData = typeof session.testTracking === 'string' 
          ? JSON.parse(session.testTracking) 
          : session.testTracking;
      } catch (error) {
        console.error("JSON parsing error:", error);
        return null;
      }
      
      let failureStep = testData.failureStep || "";
      let stepData = testData[failureStep] || {};
      
      if (!stepData || Object.keys(stepData).length === 0) {
        const cleaned = cleanStepName(failureStep);
        for (const key in testData) {
          if (cleanStepName(key) === cleaned) {
            stepData = testData[key] || {};
            break;
          }
        }
      }
      
      let [failureField, status] = processStepData(failureStep, stepData);
      failureField = failureField ? normalizeFailureName(failureField) : failureField;
      const displayStep = status === "FAIL" ? failureStep : "";
      
      return {
        "DS5_SN": serial,
        "Status": globalStatus,
        "JOB_ID": session.job_id || "",
        "RECEIPT_DATE": formatDate(session.receipt_date),
        "GRADE": grade,
        
        "TESTER_NAME": raspberry,
        "MAC_ADDRESS": session.deviceSerialNumber || "",
        "TEST_TRACKING": session.testTracking || "",
        "OPERATOR_ID": session.operator_id || "",
        "SYMPTOM": session.symptome_description || "",
  
       
        "MODE": "Jig",
        "SERIAL": serial,
        "START_DATE_FORMATTED": startDate,
        "RASPBERRY": raspberry,
        "FAILURE_STEP": displayStep,
        "FAILURE": failureField,
        "RESULT": status,
        "GLOBAL_STATUS": globalStatus,
        
      
      };
    }
  };

  const createWorkbook = (jigRows, quickRows) => {
    const wb = XLSX.utils.book_new();
    
    if (jigRows.length > 0) {
      const allJigData = jigRows.map(row => ({
        "DS5_SN": row["DS5_SN"],
        "Status": row["Status"],
        "JOB_ID": row["JOB_ID"],
        "RECEIPT_DATE": row["RECEIPT_DATE"],
        "GRADE": row["GRADE"],
        
        "TESTER_NAME": row["TESTER_NAME"],
        "MAC_ADDRESS": row["MAC_ADDRESS"],
        "TEST_TRACKING": row["TEST_TRACKING"],
        "OPERATOR_ID": row["OPERATOR_ID"],
        "SYMPTOM": row["SYMPTOM"],
        
        
        "MODE": row["MODE"],
        "SERIAL": row["SERIAL"],
        
        "RASPBERRY": row["RASPBERRY"],
        "FAILURE_STEP": row["FAILURE_STEP"],
        "FAILURE": row["FAILURE"],
        "RESULT": row["RESULT"],
        "GLOBAL_STATUS": row["GLOBAL_STATUS"],
        
      }));
      
      if (allJigData.length > 0) {
        const allJigWs = XLSX.utils.json_to_sheet(allJigData);
        XLSX.utils.book_append_sheet(wb, allJigWs, "Jig_All_Sessions");
      }
      
      const processedJigData = jigRows.map(row => ({
        "SERIAL": row["SERIAL"],
        "START_DATE": row["START_DATE_FORMATTED"],
        "RASPBERRY": row["RASPBERRY"],
        "FAILURE_STEP": row["FAILURE_STEP"],
        "FAILURE": row["FAILURE"],
        "RESULT": row["RESULT"],
        "GLOBAL_STATUS": row["GLOBAL_STATUS"],
        "GRADE": row["GRADE_FINAL"],
        "MODE": row["MODE"]
      }));
      
     
    }
    
    if (quickRows.length > 0) {
      const quickWs = XLSX.utils.json_to_sheet(quickRows);
      XLSX.utils.book_append_sheet(wb, quickWs, "Quick_Sessions");
    }
    
    return wb;
  };

  const generateFilename = () => {
    const today = new Date();
    const dateStr = today.toISOString().slice(0, 10).replace(/-/g, '_');
    const timeStr = today.getHours().toString().padStart(2, '0') + 
                   today.getMinutes().toString().padStart(2, '0');
    const modeSuffix = filters?.mode ? `_${filters.mode}` : '';
    return `sessions_${dateStr}_${timeStr}${modeSuffix}.xlsx`;
  };

  const handleAdvancedExport = async (exportAllData) => {
    try {
      setExportLoading(true);
      
      let dataToExport = exportAllData ? await fetchAllData() : data || [];
      
      if (dataToExport.length === 0) {
        alert("No data to export");
        return;
      }
      
      const jigRows = [];
      const quickRows = [];
      
      for (const session of dataToExport) {
        const processed = processSession(session);
        if (processed) {
          if (processed.MODE === "Jig") {
            jigRows.push(processed);
          } else {
            quickRows.push(processed);
          }
        }
      }
      
      const wb = createWorkbook(jigRows, quickRows);
      
      if (wb.SheetNames.length === 0) {
        alert("No valid data to export");
        return;
      }
      
      const filename = generateFilename();
      XLSX.writeFile(wb, filename);
      
      console.log(`✅ File generated: ${filename}`);
      console.log(`📊 ${jigRows.length} Jig sessions, ${quickRows.length} Quick sessions`);
      
    } catch (error) {
      console.error("Export error:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setExportLoading(false);
      setShowExportModal(false);
    }
  };

  const handleSimpleExport = async (exportAllData) => {
    try {
      setExportLoading(true);
      
      let dataToExport = exportAllData ? await fetchAllData() : data || [];
      
      if (dataToExport.length === 0) {
        alert("No data to export");
        return;
      }
      
      const columnMapping = {
        realSerialNumber: "DS5_SN",
        globalStatus: "Status",
        raspberry_id: 'TESTER_NAME',
        deviceSerialNumber: 'MAC_ADDRESS',
        job_id: 'JOB_ID',
        receipt_date: 'RECEIPT_DATE',
        symptome_description: 'SYMPTOM',
        grade: "GRADE",
        startDate: "START_DATE",
        endDate: "END_DATE"
      };
      
      const renamedData = dataToExport.map(row => {
        const newRow = {};
        for (const key in row) {
          const newKey = columnMapping[key] || key;
          newRow[newKey] = (key.includes('date') || key.includes('Date')) 
            ? formatDate(row[key]) 
            : row[key];
        }
        return newRow;
      });
      
      const ws = XLSX.utils.json_to_sheet(renamedData);
      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "Sessions");
      
      const today = new Date();
      const dateStr = today.toISOString().slice(0, 10).replace(/-/g, '_');
      const timeStr = today.getHours().toString().padStart(2, '0') + 
                     today.getMinutes().toString().padStart(2, '0');
      const filename = `export_simple_${dateStr}_${timeStr}.xlsx`;
      
      XLSX.writeFile(wb, filename);
      console.log(`✅ Simple export completed: ${filename}`);
      
    } catch (error) {
      console.error("Simple export error:", error);
      alert(`Error: ${error.message}`);
    } finally {
      setExportLoading(false);
      setShowExportModal(false);
    }
  };

  return (
    <div className="toolbar-container">
      <button 
        onClick={() => setShowExportModal(true)} 
        className="excel-export"
        disabled={exportLoading}
      >
        <FileEarmarkExcel size={20} className="excel-icon" />
        <span>{exportLoading ? 'Exporting...' : 'Export Excel'}</span>
      </button>

      {showExportModal && (
        <div className="export-modal">
          <div className="export-modal-content">
            <h3>Choose Export Option</h3>
            
            
            <div className="export-options">
              <button
                onClick={() => handleAdvancedExport(true)}
                className="export-option-btn"
                disabled={exportLoading}
              >
                <div className="option-title">
                  <strong>📥 Advanced export - All data</strong>
                </div>
                
              </button>
              
              <button
                onClick={() => handleAdvancedExport(false)}
                className="export-option-btn"
                disabled={exportLoading}
              >
                <div className="option-title">
                  <strong>📊 Advanced export - Visible data</strong>
                </div>
                
              </button>
              
              
              
              <button
                onClick={() => handleSimpleExport(false)}
                className="export-option-btn"
                disabled={exportLoading}
              >
                <div className="option-title">
                  <strong>📄 Simple export - Visible data Quick Drift</strong>
                </div>
               
              </button>
            </div>
            
            <div className="export-actions">
              <button 
                onClick={() => setShowExportModal(false)}
                className="cancel-btn"
                disabled={exportLoading}
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Toolbar;